import { useState, useRef, useEffect } from 'react';

import { localeAtom } from '@core/Atoms/Locale/Locale.atom';
import { MakeModelAtom } from '@core/Entities/Search/Aggregations/Aggregations.entity';
import { ChevronDown } from '@gds/Icons/Paths/ChevronDown';

import { areaAtom } from '@search/Atoms/AreaAtom';
import { regionAtom } from '@search/Atoms/RegionAtom';
import { RegionAreaAtom } from '@search/Atoms/RegionAtom.entity';
import { shouldShowRegionLoaderAtom } from '@search/Atoms/RegionLoadingStateAtom';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { usePathname, useRouter } from 'next/navigation';

import { locationHubPostcodeAtom } from 'Atoms/Search/PostcodeAtom/LocationHubPostcodeAtom';
import { RegionAreaProps } from 'Components/SearchWidget/Search.entity';
import { getFilteredAreas } from 'Utils/Search/GetFilteredAreas';

import styles from '../../Search.module.css';

import CustomDropDown from '../CustomDropDown/CustomDropDown';

const Area = ({
  areaPlaceholder,
  searchPlaceholder,
  noResultText,
  areaTitle,
  state,
}: RegionAreaProps) => {
  const DEFAULT_OPTION = {
    key: 'any_area',
    displayName: areaPlaceholder,
    region: 'anyarea',
    location: {
      lat: 0,
      lon: 0,
    },
    postcode: '',
    areaCode: '',
  };
  const areaRef = useRef<HTMLInputElement>(null);
  const selectedRegion = useAtomValue(regionAtom);
  const locale = useAtomValue(localeAtom);
  const router = useRouter();

  const eligibleAreas = getFilteredAreas(state.areas, selectedRegion, locale);

  const [isOpen, setIsOpen] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [filteredAreas, setFilteredAreas] = useState<any[]>([]);
  const [selectedArea, setSelectedArea] = useAtom(areaAtom);
  const setShowLoader = useSetAtom(shouldShowRegionLoaderAtom);

  const pathname = usePathname();
  const setLocationHubPostcode = useSetAtom(locationHubPostcodeAtom);
  const handleMenuOpen = () => {
    setIsOpen(isOpen => !isOpen);
    setSearchKeyword('');
  };

  useEffect(() => {
    const filteredArr =
      eligibleAreas &&
      eligibleAreas.filter(data => {
        return data.displayName.toLowerCase().includes(searchKeyword);
      });
    setFilteredAreas(searchKeyword ? filteredArr : []);
  }, [searchKeyword]);

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (!areaRef?.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
  }, [areaRef]);

  const handleSearchChange = (value: string) => {
    setSearchKeyword(value.toLowerCase());
  };

  const handleMenuClose = (option: RegionAreaAtom) => {
    setSelectedArea({
      ...option,
      areaCode: option.areaCode || '',
    });

    const areaLat = option.location.lat;
    const areaLon = option.location.lon;
    setLocationHubPostcode({
      postcode: option.postcode,
      location: { lat: areaLat, lon: areaLon },
      isValid: true,
      error: false,
    });
    setIsOpen(false);
    setShowLoader(true);
    if (pathname.includes('dealerships')) {
      const area = option.key !== 'any_area' ? option.areaCode || option.key : '';
      const region =
        selectedRegion.key !== 'any_region'
          ? selectedRegion.regionCode || selectedRegion.region
          : '';
      const path = `/dealerships/${region}/${area}`;

      return router.push(path);
    }
  };

  const isAreaDisabled = !eligibleAreas.some(
    data =>
      data.region.toLowerCase() ===
      (locale === 'uk'
        ? selectedRegion?.region.toLowerCase()
        : selectedRegion?.displayName.toLowerCase()),
  );

  return (
    <div
      className={`${styles.makeModelWrapper} ${isOpen ? styles.active : ''} ${
        isAreaDisabled ? styles.disabled : ''
      }`}
      ref={areaRef}
      data-test-id="area-component"
      aria-label="area-value"
    >
      <div
        className={styles.dropdownWrapper}
        onClick={handleMenuOpen}
        data-test-id="area-menu-button"
      >
        <span className={styles.makeValue} data-test-id="area-value">
          {selectedArea?.key === 'any_area' ? areaPlaceholder : selectedArea?.displayName}
        </span>
        <div className={styles.iconWrapper}>
          <ChevronDown className={isOpen ? styles.rotate : ''} />
        </div>
      </div>
      {isOpen && (
        <CustomDropDown
          items={searchKeyword.length > 0 ? filteredAreas : [DEFAULT_OPTION, ...eligibleAreas]}
          isGrouped={false}
          onSelect={(option: MakeModelAtom | RegionAreaAtom) =>
            handleMenuClose(option as RegionAreaAtom)
          }
          checkedValue={selectedArea?.key}
          dataTestId="area-menu"
          searchPlaceholder={searchPlaceholder}
          handleSearchChange={handleSearchChange}
          noResultText={noResultText}
          dropdownType="region"
          handleMenuOpen={handleMenuOpen}
          title={areaTitle}
        />
      )}
    </div>
  );
};
export default Area;
